import { ShowroomApplicationState, WorkflowStatus } from '@makemydeal/dr-sales-checkin-types';

/**
 * Maps the server-computed ShowroomApplicationState (from INIT_PENCIL_SUCCESS payload)
 * to the client-side WorkflowStatus used for routing decisions.
 *
 * The server and client share the same rule logic (showroomApplicationState.ts in shared-bff-utils)
 * so the values are always in sync. This mapping bridges the two vocabularies:
 *   - ShowroomApplicationState: server enum, 7 values
 *   - WorkflowStatus: client enum, superset — adds IDLE, BOOTSTRAP_ERROR,
 *     ROLE_ASSUMPTION_REQUIRED, REDIRECT_TO_SALES_VIEW (client-only routing concepts)
 *
 * Called once at bootstrap by workflowReducer on INIT_PENCIL_SUCCESS.
 * After bootstrap, the client rules (workflowEvaluator.ts) take over and re-derive
 * WorkflowStatus locally on every user action without consulting the server.
 */
const applicationStateToWorkflowStatus: Record<string, WorkflowStatus> = {
    [ShowroomApplicationState.INVALID]: WorkflowStatus.INVALID,
    [ShowroomApplicationState.MISSING_VEHICLE]: WorkflowStatus.MISSING_VEHICLE,
    [ShowroomApplicationState.IN_PROGRESS]: WorkflowStatus.IN_PROGRESS,
    [ShowroomApplicationState.SENT_FOR_DESKING]: WorkflowStatus.SENT_FOR_DESKING,
    [ShowroomApplicationState.DESKED]: WorkflowStatus.DESKED,
    [ShowroomApplicationState.ACCEPTED]: WorkflowStatus.ACCEPTED,
    // VALID is standard-mode only — client maps it to an external redirect intent
    [ShowroomApplicationState.VALID]: WorkflowStatus.REDIRECT_TO_SALES_VIEW
};

/**
 * Maps a server applicationState string to the corresponding WorkflowStatus.
 * Falls back to IN_PROGRESS if the value is absent or unrecognised (server contract violation).
 */
export const mapApplicationStateToWorkflowStatus = (appState: string | undefined): WorkflowStatus => {
    return applicationStateToWorkflowStatus[appState as string] ?? WorkflowStatus.IN_PROGRESS;
};
